/** Domain used in LMS/content preview before assessment personalization. */
export const PREVIEW_TEMPLATE_DOMAIN = 'your-company.com';

export function normalizeTranslationKey(key) {
    if (key == null) return '';
    if (Array.isArray(key)) {
        return normalizeTranslationKey(key[0]);
    }
    return String(key);
}

import {
    getTranslationOverrideFromSource,
    translationOverrideLanguageCodes,
} from '@awarego-ui/components';

export function getAssessmentCompanyDomain(assessmentStore, questionStore) {
    const fromQuestion =
        questionStore?.question?.ui?.presentation?.personalizedData
            ?.companyDomain;
    if (fromQuestion) return fromQuestion;

    let definition = assessmentStore?.assessment?.definition;
    if (typeof definition === 'string') {
        try {
            definition = JSON.parse(definition);
        } catch {
            return null;
        }
    }
    return definition?.personalizeData?.companyDomain || null;
}

function domainsForKey(normalizedKey, companyDomain) {
    const domains = new Set();
    if (companyDomain) domains.add(companyDomain);
    const match = normalizedKey.match(/@([^@\s,<]+)/);
    if (match) domains.add(match[1]);
    return [...domains];
}

/** Keys to check for overrides (personalized value, then preview template). */
export function translationLookupKeys(key, companyDomain) {
    const normalizedKey = normalizeTranslationKey(key);
    if (!normalizedKey) return [];

    const keys = [normalizedKey];

    for (const domain of domainsForKey(normalizedKey, companyDomain)) {
        if (!domain || domain === PREVIEW_TEMPLATE_DOMAIN) continue;
        if (!normalizedKey.includes(domain)) continue;
        const templateKey = normalizedKey.split(domain).join(PREVIEW_TEMPLATE_DOMAIN);
        if (templateKey !== normalizedKey && !keys.includes(templateKey)) {
            keys.push(templateKey);
        }
    }

    return keys;
}

export function lookupTranslationOverride(
    lookupKeys,
    lng,
    { questionTranslations, assessmentTranslations, branding }
) {
    const sources = [
        questionTranslations,
        assessmentTranslations,
        branding,
    ].filter(Boolean);

    for (const key of lookupKeys) {
        for (const source of sources) {
            const override = getTranslationOverrideFromSource(source, lng, key);
            if (override != null) {
                return override;
            }
        }
    }

    // Last resort for non-image keys: match ui.translation.<any-lang>.<key>
    if (branding) {
        for (const key of lookupKeys) {
            if (translationOverrideLanguageCodes(lng, key).includes('en')) {
                continue;
            }
            const suffix = `.${key}`;
            for (const [settingName, override] of Object.entries(branding)) {
                if (
                    settingName.startsWith('ui.translation.') &&
                    settingName.endsWith(suffix) &&
                    override !== undefined &&
                    override !== null &&
                    override !== ''
                ) {
                    return override;
                }
            }
        }
    }

    return null;
}
